/**
 * Settings API module — typed wrappers for /v1/settings/* endpoints.
 *
 * All calls use the shared openapi-fetch client from @/api/client.
 * NEVER call third-party APIs directly from this module.
 */
import { api } from '@/api/client';
/** GET /v1/settings — full settings snapshot. */
export async function fetchSettings() {
    const { data, error } = await api.GET('/v1/settings');
    if (error)
        throw new Error(`Failed to load settings: ${JSON.stringify(error)}`);
    if (!data)
        throw new Error('No data returned from /v1/settings');
    return data;
}
/** PATCH /v1/settings/profile */
export async function patchProfile(req) {
    const { error } = await api.PATCH('/v1/settings/profile', { body: req });
    if (error)
        throw new Error(`Failed to update profile: ${JSON.stringify(error)}`);
}
/** PATCH /v1/settings/portal */
export async function patchPortal(req) {
    const { error } = await api.PATCH('/v1/settings/portal', { body: req });
    if (error)
        throw new Error(`Failed to update portal: ${JSON.stringify(error)}`);
}
/** PATCH /v1/settings/board-policy */
export async function patchBoardPolicy(req) {
    const { error } = await api.PATCH('/v1/settings/board-policy', { body: req });
    if (error)
        throw new Error(`Failed to update board policy: ${JSON.stringify(error)}`);
}
/** PATCH /v1/settings/ai-policy */
export async function patchAiPolicy(req) {
    const { error } = await api.PATCH('/v1/settings/ai-policy', { body: req });
    if (error)
        throw new Error(`Failed to update AI policy: ${JSON.stringify(error)}`);
}
/** POST /v1/settings/logo/upload-url — returns { uploadUrl, blobName }. D-S2: direct public blob URL. */
export async function getLogoUploadUrl() {
    const { data, error } = await api.POST('/v1/settings/logo/upload-url');
    if (error)
        throw new Error(`Failed to get upload URL: ${JSON.stringify(error)}`);
    if (!data)
        throw new Error('No data returned from logo upload-url');
    return data;
}
/** POST /v1/settings/logo/confirm — registers the uploaded blob name in HoaSettings. */
export async function confirmLogoUpload(blobName) {
    const { data, error } = await api.POST('/v1/settings/logo/confirm', {
        body: { blobName },
    });
    if (error)
        throw new Error(`Failed to confirm logo upload: ${JSON.stringify(error)}`);
    if (!data)
        throw new Error('No data returned from logo confirm');
    return data.logoUrl;
}
/** GET /v1/settings/audit-log — cursor-paginated workspace audit log. */
export async function fetchAuditLog(cursor) {
    const { data, error } = await api.GET('/v1/settings/audit-log', {
        params: { query: cursor ? { cursor } : {} },
    });
    if (error)
        throw new Error(`Failed to load audit log: ${JSON.stringify(error)}`);
    if (!data)
        throw new Error('No data returned from /v1/settings/audit-log');
    return data;
}
/**
 * POST /v1/settings/export-request — D-S7: deferred.
 * Returns 501; the store catches this and shows a "Coming soon" toast.
 */
export async function requestExport() {
    await api.POST('/v1/settings/export-request');
    // 501 is the expected response — treated as "coming soon" at the call-site.
}
/**
 * DELETE /v1/settings/workspace — soft-delete with confirmCode validation.
 * Returns { deletedAt, scheduledForHardDeleteAt } on success.
 */
export async function deleteWorkspace(confirmCode) {
    const { data, error } = await api.DELETE('/v1/settings/workspace', {
        body: { confirmCode },
    });
    if (error)
        throw new Error(`Failed to delete workspace: ${JSON.stringify(error)}`);
    if (!data)
        throw new Error('No data returned from DELETE /v1/settings/workspace');
    return data;
}
/** POST /v1/settings/cancel-delete — undo a pending soft-delete. */
export async function cancelDelete() {
    const { error } = await api.POST('/v1/settings/cancel-delete');
    if (error)
        throw new Error(`Failed to cancel delete: ${JSON.stringify(error)}`);
}
